/**
 * Theme Presets Configuration
 *
 * Defines preset themes that users can switch between.
 * Each preset controls colors, typography, card styling, and component appearance.
 */

// ============================================================================
// TYPES
// ============================================================================

export interface ThemeTypography {
  fontSize: string;
  fontWeight: string;
  letterSpacing?: string;
  lineHeight?: string;
}

export interface SemanticColors {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  border: string;
  input: string;
  ring: string;
  sidebar: string;
  sidebarForeground: string;
  sidebarPrimary: string;
  sidebarPrimaryForeground: string;
  sidebarAccent: string;
  sidebarAccentForeground: string;
  sidebarBorder: string;
  sidebarRing: string;
}

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  fontFamily: string;
  fontUrl: string;
  colors: {
    primary: string;
    secondary: string;
    tertiary: string;
    accent: string;
    ring: string;
    gradient: {
      start: string;
      end: string;
      angle: number;
    };
    chart: string[];
    background: { light: string; dark: string };
    card: { light: string; dark: string };
  };
  semantic: {
    light: SemanticColors;
    dark: SemanticColors;
  };
  typography: {
    pageTitle: ThemeTypography;
    sectionTitle: ThemeTypography;
    cardTitle: ThemeTypography;
    kpiValue: ThemeTypography;
    tableHeader: ThemeTypography;
    body: ThemeTypography;
    small: ThemeTypography;
  };
  cardStyle: {
    borderRadius: string;
    padding: string;
    shadow: { light: string; dark: string };
    backdropBlur: string;
  };
  components: {
    buttonRadius: string;
    inputRadius: string;
    badgeRadius: string;
    baseRadius: string;
  };
  scrollbar: {
    width: string;
    thumbColor: string;
  };
  layout: {
    iconBoxSize: string;
    iconBoxRadius: string;
    iconSize: string;
    kpiGridMin: string;
    gridGap: string;
    contentPadding: string;
    labelSize: string;
  };
}

// ============================================================================
// MODERN PRESET (current default)
// ============================================================================

const modernPreset: ThemePreset = {
  id: "modern",
  name: "Modern",
  description: "Clean orange theme with Plus Jakarta Sans",
  fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
  fontUrl: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap",
  colors: {
    primary: "#F97316",
    secondary: "#FB923C",
    tertiary: "#EA580C",
    accent: "#FED7AA",
    ring: "#F97316",
    gradient: { start: "#FB923C", end: "#EA580C", angle: 135 },
    chart: [
      "#F97316", "#EA580C", "#FB923C", "#FDBA74",
      "#C2410C", "#FF6B35", "#D97706", "#B45309",
    ],
    background: { light: "#f5f5f5", dark: "#1a1a1a" },
    card: { light: "#ffffff", dark: "#2d2d2d" },
  },
  semantic: {
    light: {
      background: "#f5f5f5",
      foreground: "#242424",
      card: "#ffffff",
      cardForeground: "#242424",
      popover: "#ffffff",
      popoverForeground: "#242424",
      primary: "#E85B5B",
      primaryForeground: "#ffffff",
      secondary: "#f0f0f0",
      secondaryForeground: "#242424",
      muted: "#f5f5f5",
      mutedForeground: "#696969",
      accent: "linear-gradient(135deg, #F06B6B 0%, #D14545 100%)",
      accentForeground: "#ffffff",
      destructive: "#ef4444",
      border: "#e5e5e5",
      input: "#e5e5e5",
      ring: "#E85B5B",
      sidebar: "#ffffff",
      sidebarForeground: "#242424",
      sidebarPrimary: "#E85B5B",
      sidebarPrimaryForeground: "#ffffff",
      sidebarAccent: "#f5f5f5",
      sidebarAccentForeground: "#242424",
      sidebarBorder: "#e5e5e5",
      sidebarRing: "#E85B5B",
    },
    dark: {
      background: "#1a1a1a",
      foreground: "#fafafa",
      card: "#2d2d2d",
      cardForeground: "#fafafa",
      popover: "#2d2d2d",
      popoverForeground: "#fafafa",
      primary: "#E85B5B",
      primaryForeground: "#ffffff",
      secondary: "#363636",
      secondaryForeground: "#fafafa",
      muted: "#333333",
      mutedForeground: "#a3a3a3",
      accent: "linear-gradient(135deg, #F06B6B 0%, #D14545 100%)",
      accentForeground: "#ffffff",
      destructive: "#ef4444",
      border: "#3d3d3d",
      input: "#3d3d3d",
      ring: "#E85B5B",
      sidebar: "#2d2d2d",
      sidebarForeground: "#fafafa",
      sidebarPrimary: "#E85B5B",
      sidebarPrimaryForeground: "#ffffff",
      sidebarAccent: "#363636",
      sidebarAccentForeground: "#fafafa",
      sidebarBorder: "#3d3d3d",
      sidebarRing: "#E85B5B",
    },
  },
  typography: {
    pageTitle: { fontSize: "1.5rem", fontWeight: "700", letterSpacing: "-0.025em" },
    sectionTitle: { fontSize: "1.125rem", fontWeight: "600" },
    cardTitle: { fontSize: "0.875rem", fontWeight: "600", letterSpacing: "0.05em" },
    kpiValue: { fontSize: "1.875rem", fontWeight: "700", letterSpacing: "-0.025em" },
    tableHeader: { fontSize: "0.75rem", fontWeight: "500", letterSpacing: "0.05em" },
    body: { fontSize: "0.875rem", fontWeight: "400" },
    small: { fontSize: "0.75rem", fontWeight: "400" },
  },
  cardStyle: {
    borderRadius: "0.75rem",
    padding: "1.5rem",
    shadow: {
      light: "0 4px 24px -4px rgba(0, 0, 0, 0.08), 0 2px 8px -2px rgba(0, 0, 0, 0.04)",
      dark: "0 4px 24px -4px rgba(0, 0, 0, 0.3), 0 2px 8px -2px rgba(0, 0, 0, 0.2)",
    },
    backdropBlur: "none",
  },
  components: {
    buttonRadius: "0.75rem",
    inputRadius: "0.75rem",
    badgeRadius: "9999px",
    baseRadius: "0.75rem",
  },
  scrollbar: {
    width: "8px",
    thumbColor: "linear-gradient(135deg, var(--brand-gradient-start) 0%, var(--brand-gradient-end) 100%)",
  },
  layout: {
    iconBoxSize: "44px",
    iconBoxRadius: "14px",
    iconSize: "20px",
    kpiGridMin: "220px",
    gridGap: "16px",
    contentPadding: "24px 32px",
    labelSize: "0.875rem",
  },
};

// ============================================================================
// CLASSIC PRESET (reference project style)
// ============================================================================

const classicPreset: ThemePreset = {
  id: "classic",
  name: "Classic",
  description: "Inter font, glassmorphism cards, warm orange tones",
  fontFamily: "'Inter', system-ui, sans-serif",
  fontUrl: "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap",
  colors: {
    primary: "#F97316",
    secondary: "#FB923C",
    tertiary: "#EA580C",
    accent: "#FED7AA",
    ring: "#F97316",
    gradient: { start: "#FB923C", end: "#EA580C", angle: 135 },
    chart: [
      "#F97316", "#EA580C", "#FB923C", "#FDBA74",
      "#C2410C", "#FF6B35", "#D97706", "#B45309",
    ],
    background: { light: "#FAF7F7", dark: "#0a0000" },
    card: { light: "rgba(255,255,255,0.92)", dark: "rgba(40,10,10,0.55)" },
  },
  semantic: {
    light: {
      background: "#FAF7F7",
      foreground: "#1a0505",
      card: "rgba(255,255,255,0.92)",
      cardForeground: "#1a0505",
      popover: "rgba(255,255,255,0.95)",
      popoverForeground: "#1a0505",
      primary: "#dc454f",
      primaryForeground: "#ffffff",
      secondary: "#f5eded",
      secondaryForeground: "#1a0505",
      muted: "#f5eded",
      mutedForeground: "#8a6565",
      accent: "linear-gradient(135deg, #e85860 0%, #c93d46 100%)",
      accentForeground: "#ffffff",
      destructive: "#ef4444",
      border: "rgba(220,69,79,0.15)",
      input: "rgba(220,69,79,0.15)",
      ring: "#dc454f",
      sidebar: "rgba(255,255,255,0.92)",
      sidebarForeground: "#1a0505",
      sidebarPrimary: "#dc454f",
      sidebarPrimaryForeground: "#ffffff",
      sidebarAccent: "#f5eded",
      sidebarAccentForeground: "#1a0505",
      sidebarBorder: "rgba(220,69,79,0.15)",
      sidebarRing: "#dc454f",
    },
    dark: {
      background: "#0a0000",
      foreground: "#F5EAEA",
      card: "rgba(40,10,10,0.55)",
      cardForeground: "#F5EAEA",
      popover: "rgba(40,10,10,0.7)",
      popoverForeground: "#F5EAEA",
      primary: "#dc454f",
      primaryForeground: "#ffffff",
      secondary: "rgba(60,15,15,0.5)",
      secondaryForeground: "#F5EAEA",
      muted: "rgba(50,10,10,0.4)",
      mutedForeground: "#B89A9A",
      accent: "linear-gradient(135deg, #e85860 0%, #c93d46 100%)",
      accentForeground: "#ffffff",
      destructive: "#ef4444",
      border: "rgba(220,69,79,0.15)",
      input: "rgba(220,69,79,0.15)",
      ring: "#dc454f",
      sidebar: "rgba(40,10,10,0.55)",
      sidebarForeground: "#F5EAEA",
      sidebarPrimary: "#dc454f",
      sidebarPrimaryForeground: "#ffffff",
      sidebarAccent: "rgba(60,15,15,0.5)",
      sidebarAccentForeground: "#F5EAEA",
      sidebarBorder: "rgba(220,69,79,0.15)",
      sidebarRing: "#dc454f",
    },
  },
  typography: {
    pageTitle: { fontSize: "1.75rem", fontWeight: "700", letterSpacing: "-0.03em" },
    sectionTitle: { fontSize: "1.25rem", fontWeight: "600", letterSpacing: "-0.01em" },
    cardTitle: { fontSize: "0.8125rem", fontWeight: "500", letterSpacing: "0.04em" },
    kpiValue: { fontSize: "1.375rem", fontWeight: "700", letterSpacing: "-0.02em" },
    tableHeader: { fontSize: "0.6875rem", fontWeight: "500", letterSpacing: "0.06em" },
    body: { fontSize: "0.875rem", fontWeight: "400", lineHeight: "1.6" },
    small: { fontSize: "0.75rem", fontWeight: "400" },
  },
  cardStyle: {
    borderRadius: "16px",
    padding: "1.5rem",
    shadow: {
      light: "0 4px 24px rgba(0, 0, 0, 0.06)",
      dark: "0 4px 24px rgba(0, 0, 0, 0.4)",
    },
    backdropBlur: "blur(16px)",
  },
  components: {
    buttonRadius: "12px",
    inputRadius: "12px",
    badgeRadius: "9999px",
    baseRadius: "1rem",
  },
  scrollbar: {
    width: "6px",
    thumbColor: "linear-gradient(135deg, #dc454f 0%, #c93d46 100%)",
  },
  layout: {
    iconBoxSize: "44px",
    iconBoxRadius: "14px",
    iconSize: "20px",
    kpiGridMin: "220px",
    gridGap: "16px",
    contentPadding: "24px 32px",
    labelSize: "0.875rem",
  },
};

// ============================================================================
// MINIMAL PRESET
// ============================================================================

const minimalPreset: ThemePreset = {
  id: "salesgrid",
  name: "SalesGrid Original",
  description: "System font, orange accent, clean and simple",
  fontFamily: "system-ui, -apple-system, sans-serif",
  fontUrl: "",
  colors: {
    primary: "#F97316",
    secondary: "#FB923C",
    tertiary: "#EA580C",
    accent: "#FED7AA",
    ring: "#F97316",
    gradient: { start: "#FB923C", end: "#EA580C", angle: 135 },
    chart: [
      "#F97316", "#EA580C", "#FB923C", "#FDBA74",
      "#C2410C", "#FF6B35", "#D97706", "#B45309",
    ],
    background: { light: "#ffffff", dark: "#18181C" },
    card: { light: "#ffffff", dark: "#18181b" },
  },
  semantic: {
    light: {
      background: "#ffffff",
      foreground: "#09090b",
      card: "#ffffff",
      cardForeground: "#09090b",
      popover: "#ffffff",
      popoverForeground: "#09090b",
      primary: "#3B82F6",
      primaryForeground: "#ffffff",
      secondary: "#f4f4f5",
      secondaryForeground: "#09090b",
      muted: "#f4f4f5",
      mutedForeground: "#71717a",
      accent: "linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)",
      accentForeground: "#ffffff",
      destructive: "#ef4444",
      border: "#e4e4e7",
      input: "#e4e4e7",
      ring: "#3B82F6",
      sidebar: "#ffffff",
      sidebarForeground: "#09090b",
      sidebarPrimary: "#3B82F6",
      sidebarPrimaryForeground: "#ffffff",
      sidebarAccent: "#f4f4f5",
      sidebarAccentForeground: "#09090b",
      sidebarBorder: "#e4e4e7",
      sidebarRing: "#3B82F6",
    },
    dark: {
      background: "#18181C",
      foreground: "#fafafa",
      card: "#18181b",
      cardForeground: "#fafafa",
      popover: "#18181b",
      popoverForeground: "#fafafa",
      primary: "#3B82F6",
      primaryForeground: "#ffffff",
      secondary: "#27272a",
      secondaryForeground: "#fafafa",
      muted: "#27272a",
      mutedForeground: "#a1a1aa",
      accent: "linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)",
      accentForeground: "#ffffff",
      destructive: "#ef4444",
      border: "#27272a",
      input: "#27272a",
      ring: "#3B82F6",
      sidebar: "#18181b",
      sidebarForeground: "#fafafa",
      sidebarPrimary: "#3B82F6",
      sidebarPrimaryForeground: "#ffffff",
      sidebarAccent: "#27272a",
      sidebarAccentForeground: "#fafafa",
      sidebarBorder: "#27272a",
      sidebarRing: "#3B82F6",
    },
  },
  typography: {
    pageTitle: { fontSize: "1.5rem", fontWeight: "600", letterSpacing: "-0.02em" },
    sectionTitle: { fontSize: "1.125rem", fontWeight: "600" },
    cardTitle: { fontSize: "0.875rem", fontWeight: "500" },
    kpiValue: { fontSize: "1.75rem", fontWeight: "700", letterSpacing: "-0.02em" },
    tableHeader: { fontSize: "0.75rem", fontWeight: "500", letterSpacing: "0.04em" },
    body: { fontSize: "0.875rem", fontWeight: "400" },
    small: { fontSize: "0.75rem", fontWeight: "400" },
  },
  cardStyle: {
    borderRadius: "12px",
    padding: "1.5rem",
    shadow: {
      light: "0 1px 3px rgba(0, 0, 0, 0.05)",
      dark: "0 1px 3px rgba(0, 0, 0, 0.2)",
    },
    backdropBlur: "none",
  },
  components: {
    buttonRadius: "8px",
    inputRadius: "8px",
    badgeRadius: "6px",
    baseRadius: "0.75rem",
  },
  scrollbar: {
    width: "8px",
    thumbColor: "linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)",
  },
  layout: {
    iconBoxSize: "44px",
    iconBoxRadius: "14px",
    iconSize: "20px",
    kpiGridMin: "220px",
    gridGap: "16px",
    contentPadding: "24px 32px",
    labelSize: "0.875rem",
  },
};

// ============================================================================
// SALESGRID PRESET (Orange theme)
// ============================================================================

const salesGridPreset: ThemePreset = {
  id: "minimal",
  name: "Minimal",
  description: "SalesGrid dashboard design with vibrant orange theme",
  fontFamily: "'Inter', system-ui, sans-serif",
  fontUrl: "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap",
  colors: {
    primary: "#F04911",
    secondary: "#F76035",
    tertiary: "#C93B0C",
    accent: "#F89A7A",
    ring: "#F04911",
    gradient: { start: "#F76035", end: "#C93B0C", angle: 135 },
    chart: [
      "#F04911", "#F76035", "#C93B0C", "#D8501E",
      "#A83008", "#E86A38", "#8F2706", "#F47D50",
    ],
    background: { light: "#FFF7EE", dark: "#0a0500" },
    card: { light: "rgba(255,255,255,0.92)", dark: "rgba(40,25,10,0.55)" },
  },
  semantic: {
    light: {
      background: "#FFF7EE",
      foreground: "#1a0f05",
      card: "rgba(255,255,255,0.92)",
      cardForeground: "#1a0f05",
      popover: "rgba(255,255,255,0.95)",
      popoverForeground: "#1a0f05",
      primary: "#F04911",
      primaryForeground: "#ffffff",
      secondary: "#FFF1E4",
      secondaryForeground: "#1a0f05",
      muted: "#FFF7EE",
      mutedForeground: "#8a6545",
      accent: "linear-gradient(135deg, #F76035 0%, #C93B0C 100%)",
      accentForeground: "#ffffff",
      destructive: "#ef4444",
      border: "rgba(240,73,17,0.12)",
      input: "rgba(240,73,17,0.12)",
      ring: "#F04911",
      sidebar: "#FFFFFF",
      sidebarForeground: "#1a0f05",
      sidebarPrimary: "#F04911",
      sidebarPrimaryForeground: "#ffffff",
      sidebarAccent: "#FFF1E4",
      sidebarAccentForeground: "#1a0f05",
      sidebarBorder: "rgba(240,73,17,0.1)",
      sidebarRing: "#F04911",
    },
    dark: {
      background: "#0a0500",
      foreground: "#F5EAE0",
      card: "rgba(40,25,10,0.55)",
      cardForeground: "#F5EAE0",
      popover: "rgba(40,25,10,0.7)",
      popoverForeground: "#F5EAE0",
      primary: "#F04911",
      primaryForeground: "#ffffff",
      secondary: "rgba(60,35,15,0.5)",
      secondaryForeground: "#F5EAE0",
      muted: "rgba(50,30,10,0.4)",
      mutedForeground: "#B8A090",
      accent: "linear-gradient(135deg, #F76035 0%, #C93B0C 100%)",
      accentForeground: "#ffffff",
      destructive: "#ef4444",
      border: "rgba(240,73,17,0.15)",
      input: "rgba(240,73,17,0.15)",
      ring: "#F04911",
      sidebar: "#0d0500",
      sidebarForeground: "#F5EAE0",
      sidebarPrimary: "#F04911",
      sidebarPrimaryForeground: "#ffffff",
      sidebarAccent: "rgba(60,35,15,0.5)",
      sidebarAccentForeground: "#F5EAE0",
      sidebarBorder: "rgba(240,73,17,0.15)",
      sidebarRing: "#F04911",
    },
  },
  typography: {
    pageTitle: { fontSize: "1.625rem", fontWeight: "800", letterSpacing: "-0.03em" },
    sectionTitle: { fontSize: "0.9375rem", fontWeight: "700" },
    cardTitle: { fontSize: "0.8125rem", fontWeight: "500" },
    kpiValue: { fontSize: "1.75rem", fontWeight: "700", letterSpacing: "-0.02em" },
    tableHeader: { fontSize: "0.6875rem", fontWeight: "600", letterSpacing: "0.05em" },
    body: { fontSize: "0.8125rem", fontWeight: "500" },
    small: { fontSize: "0.75rem", fontWeight: "400" },
  },
  cardStyle: {
    borderRadius: "16px",
    padding: "1.5rem",
    shadow: {
      light: "0 1px 3px rgba(0, 0, 0, 0.08)",
      dark: "0 4px 24px rgba(0, 0, 0, 0.4)",
    },
    backdropBlur: "blur(16px)",
  },
  components: {
    buttonRadius: "10px",
    inputRadius: "10px",
    badgeRadius: "20px",
    baseRadius: "1rem",
  },
  scrollbar: {
    width: "6px",
    thumbColor: "linear-gradient(135deg, #FB923C 0%, #EA580C 100%)",
  },
  layout: {
    iconBoxSize: "40px",
    iconBoxRadius: "12px",
    iconSize: "18px",
    kpiGridMin: "200px",
    gridGap: "16px",
    contentPadding: "28px 32px",
    labelSize: "0.8125rem",
  },
};

// ============================================================================
// EXPORTS
// ============================================================================

export const THEME_PRESETS: ThemePreset[] = [modernPreset, salesGridPreset, classicPreset, minimalPreset];

export function getPresetById(id: string): ThemePreset | undefined {
  return THEME_PRESETS.find((p) => p.id === id);
}

export function mergeCustomTheme(
  base: ThemePreset,
  overrides: Partial<ThemePreset>
): ThemePreset {
  return {
    ...base,
    ...overrides,
    colors: {
      ...base.colors,
      ...(overrides.colors ?? {}),
      gradient: {
        ...base.colors.gradient,
        ...(overrides.colors?.gradient ?? {}),
      },
      background: {
        ...base.colors.background,
        ...(overrides.colors?.background ?? {}),
      },
      card: {
        ...base.colors.card,
        ...(overrides.colors?.card ?? {}),
      },
    },
    semantic: {
      light: { ...base.semantic.light, ...(overrides.semantic?.light ?? {}) },
      dark: { ...base.semantic.dark, ...(overrides.semantic?.dark ?? {}) },
    },
    typography: {
      ...base.typography,
      ...(overrides.typography ?? {}),
    },
    cardStyle: {
      ...base.cardStyle,
      ...(overrides.cardStyle ?? {}),
      shadow: {
        ...base.cardStyle.shadow,
        ...(overrides.cardStyle?.shadow ?? {}),
      },
    },
    components: {
      ...base.components,
      ...(overrides.components ?? {}),
    },
    scrollbar: {
      ...base.scrollbar,
      ...(overrides.scrollbar ?? {}),
    },
    layout: {
      ...base.layout,
      ...(overrides.layout ?? {}),
    },
    // Preserve base id/name if not overridden
    id: overrides.id ?? base.id,
    name: overrides.name ?? base.name,
    description: overrides.description ?? base.description,
    fontFamily: overrides.fontFamily ?? base.fontFamily,
    fontUrl: overrides.fontUrl ?? base.fontUrl,
  };
}
